import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const SecuritySettings: React.FC = () => {
  const colors = getThemeColors('dark');

  const [securitySettings, setSecuritySettings] = useState({
    biometric_login: false,
    two_factor_auth: false,
    auto_logout: true,
    login_notifications: true,
    suspicious_activity_alerts: true,
    device_management: true,
  });

  const [loading, setLoading] = useState(false);

  const handleSettingChange = (setting: string, value: boolean) => {
    setSecuritySettings(prev => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Security settings updated successfully!');
    } catch (error) {
      console.error('Failed to save security settings:', error);
      alert('Failed to update security settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = () => {
    window.location.href = '/dashboard/settings/change-password';
  };

  const handleViewLoginHistory = () => {
    window.location.href = '/dashboard/settings/login-history';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    settingItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.xl,
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: spacing.lg,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('md'),
      transition: 'all 0.2s ease',
    },
    settingContent: {
      flex: 1,
    },
    settingTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    settingDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
    toggle: {
      width: 60,
      height: 32,
      borderRadius: 16,
      padding: 2,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
    },
    toggleActive: {
      backgroundColor: colors.primary,
    },
    toggleInactive: {
      backgroundColor: colors.border,
    },
    toggleCircle: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.white,
      position: 'absolute',
      top: 2,
      transition: 'all 0.3s ease',
    },
    toggleCircleActive: {
      right: 2,
    },
    toggleCircleInactive: {
      left: 2,
    },
    actionItem: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
      borderRadius: borderRadius.lg,
      backgroundColor: colors.surface,
      marginBottom: spacing.sm,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    actionItemHover: {
      backgroundColor: colors.backgroundSecondary,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${colors.shadow}`,
    },
    actionContent: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    actionIcon: {
      width: 56,
      height: 56,
      borderRadius: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      fontSize: 24,
    },
    actionText: {
      flex: 1,
    },
    actionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    actionDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    actionArrow: {
      color: colors.primary,
      fontSize: 20,
      fontWeight: 'bold',
    },
    saveButton: {
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      border: 'none',
      width: '100%',
      marginTop: spacing.xl,
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    primaryButtonHover: {
      backgroundColor: colors.primary,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px ${colors.primary}40`,
    },
  };

  const renderToggle = (isActive: boolean) => (
    <div
      style={{
        ...styles.toggle,
        ...(isActive ? styles.toggleActive : styles.toggleInactive),
      }}
    >
      <div
        style={{
          ...styles.toggleCircle,
          ...(isActive ? styles.toggleCircleActive : styles.toggleCircleInactive),
        }}
      />
    </div>
  );

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Security Settings"
        subtitle="Manage your account security and privacy"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>🛡️</div>
            <h1 style={styles.headerTitle}>Security Settings</h1>
            <p style={styles.headerSubtitle}>Manage your account security and privacy</p>
          </div>

          {/* Authentication Settings */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Authentication</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingContent}>
                <div style={styles.settingTitle}>Two-Factor Authentication</div>
                <div style={styles.settingDescription}>Add an extra layer of security to your account</div>
              </div>
              <div
                onClick={() => handleSettingChange('two_factor_auth', !securitySettings.two_factor_auth)}
              >
                {renderToggle(securitySettings.two_factor_auth)}
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingContent}>
                <div style={styles.settingTitle}>Auto Logout</div>
                <div style={styles.settingDescription}>Automatically log out after period of inactivity</div>
              </div>
              <div
                onClick={() => handleSettingChange('auto_logout', !securitySettings.auto_logout)}
              >
                {renderToggle(securitySettings.auto_logout)}
              </div>
            </div>
          </div>

          {/* Security Monitoring */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Security Monitoring</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingContent}>
                <div style={styles.settingTitle}>Login Notifications</div>
                <div style={styles.settingDescription}>Get notified when someone logs into your account</div>
              </div>
              <div
                onClick={() => handleSettingChange('login_notifications', !securitySettings.login_notifications)}
              >
                {renderToggle(securitySettings.login_notifications)}
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingContent}>
                <div style={styles.settingTitle}>Suspicious Activity Alerts</div>
                <div style={styles.settingDescription}>Receive alerts for unusual account activity</div>
              </div>
              <div
                onClick={() => handleSettingChange('suspicious_activity_alerts', !securitySettings.suspicious_activity_alerts)}
              >
                {renderToggle(securitySettings.suspicious_activity_alerts)}
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingContent}>
                <div style={styles.settingTitle}>Device Management</div>
                <div style={styles.settingDescription}>Monitor and manage devices that access your account</div>
              </div>
              <div
                onClick={() => handleSettingChange('device_management', !securitySettings.device_management)}
              >
                {renderToggle(securitySettings.device_management)}
              </div>
            </div>
          </div>

          {/* Security Actions */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Security Actions</h2>

            <div
              style={styles.actionItem}
              onClick={handleChangePassword}
            >
              <div style={styles.actionContent}>
                <div style={{ ...styles.actionIcon, backgroundColor: colors.warning + '20', color: colors.warning }}>
                  🔑
                </div>
                <div style={styles.actionText}>
                  <div style={styles.actionTitle}>Change Password</div>
                  <div style={styles.actionDescription}>Update your account password</div>
                </div>
              </div>
              <span style={styles.actionArrow}>▶️</span>
            </div>

            <div
              style={styles.actionItem}
              onClick={handleViewLoginHistory}
            >
              <div style={styles.actionContent}>
                <div style={{ ...styles.actionIcon, backgroundColor: colors.info + '20', color: colors.info }}>
                  📊
                </div>
                <div style={styles.actionText}>
                  <div style={styles.actionTitle}>Login History</div>
                  <div style={styles.actionDescription}>View recent login activity</div>
                </div>
              </div>
              <span style={styles.actionArrow}>▶️</span>
            </div>
          </div>

          {/* Save Button */}
          <button
            style={{ ...styles.saveButton, ...styles.primaryButton }}
            onClick={handleSaveSettings}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;